import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { DataLayer } from '@grandvisionhq/analytics';
import { MediaQuery } from '@grandvisionhq/common';
import { useCartDiscounts, sortByMinimumQuantityAscending, useProductViewHistory } from '@grandvisionhq/fun-products';
import { useIntl, useFeatureToggles } from '@grandvisionhq/state';
import { getAttrValue, roundNumber, getLocalizedValue } from '@grandvisionhq/utils';
import { pushQuantityDiscountSelectionEvent, pushTabClickEvent, pushPdpEventToDataLayer } from '../../DataLayer/data-layer.js';
import { useLenses } from './hooks.js';
import Lenses$1 from './Lenses.js';
import { useLensBundle } from './LensBundles/context.js';
import { hasNonOrderableProduct } from './utils/has-non-orderable-product.js';
import { CTAButton } from './components/CTAButton/CTAButton.js';

const LensesContainer = ({ addToCartRef , deliveryResolverFactory , onCTAClick , product , showStickyButton , variant , handleConversionEvent ,  })=>{
    const { excludedFromCheckout , promotionalText  } = variant;
    const { masterVariant  } = product;
    const { currency , getLabel  } = useIntl();
    const { isContactLensEnabled: isPrescriptionEnabled , isDeliveryFullDateEnabled , isCartDiscountsEnabled , isPrescriptionInfoEnabled ,  } = useFeatureToggles();
    const brandKey = String(getAttrValue(masterVariant, 'brandKey')) // aka "variantGroup"
    ;
    const { cartDiscounts  } = useCartDiscounts(brandKey, isCartDiscountsEnabled);
    const sortedCartDiscounts = cartDiscounts.sort(sortByMinimumQuantityAscending);
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const productViewHistory = useProductViewHistory();
    const [activeTabIndex, setActiveTabIndex] = React.useState(0);
    /**
   * CLBB way of handling subscriptions, currently in use by the BeNe opco's.
   */ const isCLBBTabSelected = activeTabIndex === 1;
    const [lensState, lensActions] = useLenses({
        product,
        cartDiscounts,
        isPrescriptionEnabled,
        isPrescriptionInfoEnabled,
        variant
    });
    const { lineItems: bundleLineItems , pricePerMonth  } = useLensBundle();
    const hasNonOrderableProductEnabled = hasNonOrderableProduct({
        selectedLensVariants: lensState.selectedLensVariants,
        priceTotals: lensState.priceTotals
    });
    const lineItemsForCart = isCLBBTabSelected ? bundleLineItems : lensState.priceTotals.selectedProducts;
    const { selectedQuantityDiscount  } = lensState;
    const { locale  } = useIntl();
    /**
   * Add SKU to view history
   * Only adds the masterProduct so we don't get duplicates
   */ React.useEffect(()=>{
        if (Boolean(getAttrValue(masterVariant, 'masterProduct')) && masterVariant.sku) {
            productViewHistory.add(masterVariant.sku);
        }
    }, []);
    React.useEffect(()=>{
        if (!selectedQuantityDiscount) return;
        const { selectionType , item , prices  } = selectedQuantityDiscount;
        if (selectionType === 'user' && prices !== undefined) {
            pushQuantityDiscountSelectionEvent(variant.sku, getLocalizedValue(product.name, locale), prices === null || prices === void 0 ? void 0 : prices.originalUnitPrice, prices === null || prices === void 0 ? void 0 : prices.discountedUnitPrice, roundNumber(item.amount, 0));
        }
    }, [
        activeTabIndex,
        lensState.selectedQuantityDiscount
    ]) // eslint-disable-line react-hooks/exhaustive-deps
    ;
    const setActiveTab = (index)=>{
        const willCLBBbeSelected = index === 1;
        pushTabClickEvent(willCLBBbeSelected);
        setActiveTabIndex(index);
        const purchaseType = willCLBBbeSelected ? DataLayer.DataLayerTypes.PurchaseType.CLBBSubscription : DataLayer.DataLayerTypes.PurchaseType.OneTime;
        const productType = willCLBBbeSelected ? DataLayer.DataLayerTypes.LineItemType.Bundle : DataLayer.DataLayerTypes.LineItemType.Single;
        pushPdpEventToDataLayer(purchaseType, product, variant.sku, currency, productType, lensState.clbbPrice);
    };
    const hasQuantityDiscountBeenSelected = !!sortedCartDiscounts.length ? Boolean(selectedQuantityDiscount === null || selectedQuantityDiscount === void 0 ? void 0 : selectedQuantityDiscount.item) : undefined;
    const ctaButton = /*#__PURE__*/ React.createElement(CTAButton, {
        calculatedDiscountPrice: lensState.priceTotals.calculatedDiscountPrice,
        calculatedPrice: lensState.priceTotals.calculatedPrice,
        lineItemsForCart: lineItemsForCart,
        excludedFromCheckout: excludedFromCheckout,
        isPrescriptionEnabled: isPrescriptionEnabled,
        isCLBBSubscription: isCLBBTabSelected,
        hasLenses: lensState.hasLenses,
        hasNonOrderableProduct: hasNonOrderableProductEnabled,
        onPrescriptionClick: lensActions.handlePrescriptionClick,
        handleMiniCartChange: lensActions.handleMiniCartChange,
        onClick: onCTAClick,
        pricePerMonth: pricePerMonth,
        handleConversionEvent: handleConversionEvent,
        isQuantityDiscountSelected: hasQuantityDiscountBeenSelected
    });
    const contactLensesConfiguratorConfig = {
        formValidations: lensState.formValidations,
        handleFormChange: lensActions.handleFormChange,
        promotionalText,
        quantityDiscountItems: sortedCartDiscounts
    };
    let deliveryIndicatorConfig = {
        fallbackLabel: getLabel('products.deliveryIndicator.selectPrescription'),
        className: 'product-detail__delivery-indicator'
    };
    if (isDeliveryFullDateEnabled) {
        deliveryIndicatorConfig = swcHelpers.objectSpread({}, deliveryIndicatorConfig, {
            items: lineItemsForCart.map((item)=>({
                    sku: item.sku,
                    quantity: item.quantity || 1
                })
            )
        });
    } else {
        deliveryIndicatorConfig = swcHelpers.objectSpread({}, deliveryIndicatorConfig, {
            // TODO: Add availability info when bundles have that
            availability: !isCLBBTabSelected ? lensState.availability : [
                undefined
            ],
            deliveryResolver: deliveryResolverFactory && deliveryResolverFactory(lensState.selectedLensVariants)
        });
    }
    const labels = {
        perBox: getLabel('products.perBox'),
        perMonth: getLabel('common.frequency.perMonth'),
        descriptionTitle: getLabel('products.contactLenses.header.details'),
        clbbSubscriptionTitle: getLabel('products.contactLenses.subscription.title'),
        oneTimeTitle: getLabel('products.contactLenses.oneTime.title'),
        lens: getLabel('products.unit.contactLens'),
        lenses: getLabel('products.unit.contactLenses')
    };
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Lenses$1, {
        addToCartRef: addToCartRef,
        contactLensesConfiguratorConfig: contactLensesConfiguratorConfig,
        ctaButton: ctaButton,
        currency: currency,
        deliveryIndicatorConfig: deliveryIndicatorConfig,
        isSmallScreen: isSmallScreen,
        labels: labels,
        packPrice: lensState.packPrice,
        clbbPrice: lensState.clbbPrice,
        showStickyButton: showStickyButton,
        variant: variant,
        activeTabIndex: activeTabIndex,
        setActiveTabIndex: setActiveTab,
        isCLBBTabSelected: isCLBBTabSelected
    }));
};
var Lenses = LensesContainer;

export { LensesContainer, Lenses as default };
//# sourceMappingURL=LensesContainer.js.map
